// @flow

import React from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import { Page } from "react-page-layout";
import { formatAuthoredBy } from "@transformers/names";
import { Text, Row, Col, SectionComponent, Loader, View } from "@components";
import sharedActionCreators from "@modules/shared/actionCreator";
import * as sharedSelectors from "@modules/shared/selectors";
import * as userPermissionsSelectors from "@modules/service/selectors/user-permissions";
import { getRouteParams } from "@selectors";
import {
  AbsenceStatisticTable,
  AbsenceStatisticWeekday,
  AbsenceStatisticsFilter,
  AbsenceStatisticReportingCompliance,
  AbsenceStatisticClosingCompliance,
  AbsenceStatisticPerEmployee,
  AbsenceStatisticFitNoteCompliance,
  AbsenceStatisticPerReason,
} from "@modules/shared/components";
import withErrorHandler from "@components/error";
import type { State } from "@store/reducers";
import type { AbsenceStatistics } from "@modules/shared/reducer/employee-absences/types";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components";
import { getEmployee } from "@modules/employee/selectors";
import type { EmployeeType } from "@modules/employee/types";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import employeeActionCreators from "@modules/employee/actionCreator";
import { isFirstCareUser } from "@modules/account/selectors";
import type { EffectiveUserPermissions } from "@modules/service/reducer/user-permissions/types";

type PropsFromState = $Exact<{
  employeeId: number,
  employee: ?EmployeeType,
  data: ?AbsenceStatistics,
  isLoading: boolean,
  effectiveUserPermissions: ?EffectiveUserPermissions,
  isFirstCareUser: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof employeeActionCreators.GetEmployees,
  getEmployeeAbsenceStatistics: typeof sharedActionCreators.GetAbsenceStatistics,
}>;

type Props = $Exact<{
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  ...PropsFromClientContext,
  ...PropsFromPermissions,
}>;

export class EmployeeStats extends React.Component<Props> {
  componentDidMount() {
    this.props.getEmployees({
      clientId: this.props.clientId,
      filters: {
        id: this.props.employeeId,
      },
    });

    this.props.getEmployeeAbsenceStatistics({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
  }

  handleFiltersSubmit = () => {
    this.props.getEmployeeAbsenceStatistics({
      clientId: this.props.clientId,
      employeeId: this.props.employeeId,
    });
  };

  render() {
    return (
      <View position="relative" gap="4">
        {this.props.isLoading && <Loader inline />}
        <Text className="message-box" text={this.props.t("EMPLOYEE_STATISTICS_TAB_INFO")} />

        <AbsenceStatisticsFilter onSubmit={this.handleFiltersSubmit} />

        {this.props.data && !this.props.isLoading && (
          <View gap={6}>
            <AbsenceStatisticTable
              data={this.props.data}
              effectiveUserPermissions={this.props.effectiveUserPermissions}
              showAdditionalColumns
            />

            <View>
              <AbsenceStatisticWeekday data={this.props.data} />
            </View>

            <View>
              <AbsenceStatisticPerReason data={this.props.data} />
            </View>

            <View gap={6} direction="row">
              <AbsenceStatisticReportingCompliance data={this.props.data} />
              <AbsenceStatisticFitNoteCompliance data={this.props.data} />
            </View>

            <View gap={6} direction="row">
              <AbsenceStatisticClosingCompliance data={this.props.data} />
              <AbsenceStatisticPerEmployee data={this.props.data} />
            </View>
          </View>
        )}
      </View>
    );
  }
}

const mapStateToProps = (state: State, ownProps): PropsFromState => {
  const routeParams = getRouteParams(ownProps);
  const employeeId = parseInt(routeParams.employeeId);

  return {
    employeeId,
    employee: getEmployee(employeeId)(state),
    data: sharedSelectors.getAbsenceStatistics(state),
    isLoading: sharedSelectors.isLoading(state),
    isFirstCareUser: isFirstCareUser(state),
    effectiveUserPermissions: userPermissionsSelectors.getEffectiveUserPermissions(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: employeeActionCreators.GetEmployees,
  getEmployeeAbsenceStatistics: sharedActionCreators.GetAbsenceStatistics,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions({ permissionId: PermissionEnum.ACCESS_REPORTING_EMPLOYEES }),
  withClientContext(),
)(EmployeeStats);
